import React, { useState, useEffect, useCallback } from "react";
import { applyPermission, getLeaveBalance, getPermissionHistory, cancelPermission } from "../../../services/attendanceApi";
import { useToast } from "./Toast";
import Icons from "./Icons";

/**
 * PermissionForm — apply for a short permission (partial-day time off).
 *
 * Limit-aware: reads the monthly permission usage/limit (from the balance
 * endpoint, if the backend returns it) and DISABLES the form when the
 * monthly permission limit is reached, showing "Permission not available".
 */
function PermissionForm({ refreshKey }) {
  const { toast } = useToast();
  const [form, setForm] = useState({ permDate: "", fromTime: "", toTime: "", reason: "" });
  const [permInfo, setPermInfo] = useState(null); // { usedMinutes, limitMinutes }
  const [submitting, setSubmitting] = useState(false);
  const [msg, setMsg] = useState("");
  const [error, setError] = useState("");
  // Defensive history: only shown if the backend route exists.
  const [history, setHistory] = useState(null); // null = hidden/unavailable, [] = loaded empty
  const [cancelling, setCancelling] = useState(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Try to read permission usage from the balance endpoint (optional field)
  const loadPermInfo = useCallback(async () => {
    try {
      const res = await getLeaveBalance();
      const data = res && res.data ? res.data : res;
      // Backend returns FLAT keys (in MINUTES):
      //   permission_limit, permission_used, permission_remaining
      // Read these directly; fall back to the older nested shape only if the
      // flat keys are missing (safety for older backends).
      const flatLimit = data && (data.permission_limit ?? data.permissionLimit);
      const flatUsed = data && (data.permission_used ?? data.permissionUsed);
      const nested = data && (data.permission || data.permissions || data.permissionUsage);
      if (flatLimit !== undefined && flatLimit !== null) {
        setPermInfo({
          usedMinutes: Number(flatUsed ?? 0),
          limitMinutes: Number(flatLimit ?? 0),
        });
      } else if (nested) {
        setPermInfo({
          usedMinutes: Number(nested.usedMinutes ?? nested.used_minutes ?? nested.used ?? 0),
          limitMinutes: Number(nested.limitMinutes ?? nested.limit_minutes ?? nested.monthlyLimitMinutes ?? nested.monthly_limit_minutes ?? nested.limit ?? 0),
        });
      } else {
        setPermInfo(null);
      }
    } catch {
      setPermInfo(null); // unknown — don't block; backend still validates
    }
  }, []);

  useEffect(() => { loadPermInfo(); }, [loadPermInfo, refreshKey]);

  // Defensive: attempt to load permission history. If the route doesn't
  // exist yet (404) or errors, keep history null so the section stays hidden.
  const loadHistory = useCallback(async () => {
    try {
      const res = await getPermissionHistory();
      const data = res && res.data ? res.data : res;
      const list = Array.isArray(data) ? data : (data.permissions || data.history || []);
      setHistory(list);
    } catch {
      setHistory(null); // route unavailable — hide section silently
    }
  }, []);

  useEffect(() => { loadHistory(); }, [loadHistory, refreshKey]);

  // Duration of the currently entered request (minutes)
  const durationMin = () => {
    if (!form.fromTime || !form.toTime) return 0;
    const [fh, fm] = form.fromTime.split(":").map(Number);
    const [th, tm] = form.toTime.split(":").map(Number);
    return (th * 60 + tm) - (fh * 60 + fm);
  };

  const limitReached =
    permInfo && permInfo.limitMinutes > 0 && permInfo.usedMinutes >= permInfo.limitMinutes;

  const remainingMin =
    permInfo && permInfo.limitMinutes > 0
      ? Math.max(0, permInfo.limitMinutes - permInfo.usedMinutes)
      : null;

  const handleSubmit = async () => {
    setMsg("");
    setError("");
    if (limitReached) {
      setError("Monthly permission limit reached. Permission not available this month.");
      return;
    }
    if (!form.permDate || !form.fromTime || !form.toTime) {
      setError("Please fill date, from time and to time.");
      return;
    }
    if (form.toTime <= form.fromTime) {
      setError("To time must be after from time.");
      return;
    }
    if (remainingMin !== null && durationMin() > remainingMin) {
      setError(`This request (${durationMin()} min) exceeds your remaining ${remainingMin} min for the month.`);
      return;
    }
    try {
      setSubmitting(true);
      await applyPermission({
        permDate: form.permDate,
        fromTime: form.fromTime,
        toTime: form.toTime,
        reason: form.reason.trim(),
      });
      setMsg("Permission request submitted for approval.");
      setForm({ permDate: "", fromTime: "", toTime: "", reason: "" });
      loadPermInfo();
      loadHistory();
    } catch (err) {
      setError(err.message || "Could not submit permission request.");
    } finally {
      setSubmitting(false);
    }
  };

  // Cancel/withdraw a PENDING permission request.
  // Depends on backend route PUT /permission/cancel/{id} (added in parallel).
  const handleCancel = async (id) => {
    if (!id) return;
    try {
      setCancelling(id);
      await cancelPermission(id);
      toast.success("Permission request cancelled.");
      loadHistory();
      loadPermInfo();
    } catch (err) {
      toast.error(err.message || "Could not cancel permission request.");
    } finally {
      setCancelling(null);
    }
  };

  return (
    <div className="atl-card atl-permform">
      <h3><span className="atl-h3ic">{Icons.permission}</span>Apply for Permission</h3>

      {/* Monthly limit status */}
      {permInfo && permInfo.limitMinutes > 0 && (
        <div className={`atl-perm-status ${limitReached ? "is-full" : ""}`}>
          {limitReached
            ? "Monthly permission limit reached — not available this month."
            : `${remainingMin} of ${permInfo.limitMinutes} min remaining this month`}
        </div>
      )}

      {msg && <div className="atl-success">{msg}</div>}
      {error && <div className="atl-error">{error}</div>}

      {limitReached ? (
        <div className="atl-empty">You have used all your permission time for this month.</div>
      ) : (
        <>
          <div className="atl-field">
            <label>Date</label>
            <input type="date" className="atl-input" value={form.permDate} onChange={(e) => set("permDate", e.target.value)} />
          </div>
          <div className="atl-row">
            <div className="atl-field">
              <label>From Time</label>
              <input type="time" className="atl-input" value={form.fromTime} onChange={(e) => set("fromTime", e.target.value)} />
            </div>
            <div className="atl-field">
              <label>To Time</label>
              <input type="time" className="atl-input" value={form.toTime} onChange={(e) => set("toTime", e.target.value)} />
            </div>
          </div>
          <div className="atl-field">
            <label>Reason</label>
            <textarea className="atl-input" rows={2} value={form.reason} onChange={(e) => set("reason", e.target.value)} placeholder="Reason for permission" />
          </div>
          <button className="atl-btn atl-btn--primary" onClick={handleSubmit} disabled={submitting}>
            {submitting ? "Submitting..." : "Submit for Approval"}
          </button>
        </>
      )}

      {/* Recent permission requests — wired defensively; only rendered
          when GET /permission/history exists and returns data. */}
      {history && history.length > 0 && (
        <div className="atl-perm-history">
          <h4 className="atl-subhead">Recent Requests</h4>
          <div className="atl-table-wrap">
            <table className="atl-table">
              <thead>
                <tr><th>Date</th><th>From</th><th>To</th><th>Status</th><th></th></tr>
              </thead>
              <tbody>
                {history.map((h, i) => {
                  const status = (h.status || "").toUpperCase();
                  const id = h.id || h.permission_id;
                  return (
                    <tr key={h.id || h.permission_id || i}>
                      <td data-label="Date">{fmtDate(h.perm_date || h.permDate)}</td>
                      <td data-label="From">{fmtTime(h.from_time || h.fromTime)}</td>
                      <td data-label="To">{fmtTime(h.to_time || h.toTime)}</td>
                      <td data-label="Status"><span className={statusClass(status)}>{status || "—"}</span></td>
                      <td data-label="">
                        {status === "PENDING" && id && (
                          <button className="atl-link-btn" onClick={() => handleCancel(id)} disabled={cancelling === id}>
                            {cancelling === id ? "..." : "Cancel"}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function statusClass(s) {
  switch ((s || "").toUpperCase()) {
    case "APPROVED": return "atl-pill atl-pill--present";
    case "PENDING": return "atl-pill atl-pill--late";
    case "REJECTED": return "atl-pill atl-pill--absent";
    // Cancelled requests remain in history (not deleted) with a muted badge.
    case "CANCELLED": return "atl-pill atl-pill--muted";
    default: return "atl-pill";
  }
}

function fmtDate(d) {
  if (!d) return "—";
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return d;
  return dt.toLocaleDateString("en-IN", { timeZone: "Asia/Kolkata", day: "2-digit", month: "short" });
}

function fmtTime(iso) {
  // Backend returns from_time/to_time as TIME strings like "10:30:00".
  // Slice to "HH:MM" rather than feeding into new Date() (which fails on a
  // bare time string and showed "—").
  return iso ? String(iso).slice(0, 5) : "—";
}

export default PermissionForm;
